import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, Heart, Shield, Lightbulb, Handshake } from 'lucide-react';
import { leadership, values, organizationInfo, boardMembers } from '../mockData';

const AboutPage = () => {
  const valueIcons = {
    "Compassion": Heart,
    "Evidence-Based Practice": Award,
    "Peer-Driven Support": Users,
    "Trauma-Informed Care": Shield,
    "Community Collaboration": Handshake,
    "Whole-Person Wellness": Lightbulb
  };

  return (
    <div className="pt-20">
      {/* Page Header */}
      <section className="py-16 bg-gray-50">
        <div className="container text-center">
          <h1 className="heading-1 mb-6">About Saged Recovery</h1>
          <p className="body-large max-w-3xl mx-auto">
            Founded on the belief that recovery is possible for everyone, Saged Recovery and Wellness Collaborative 
            brings together lived experience, professional training, and community support to create lasting pathways to wellness.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="heading-2 mb-6">Our Mission</h2>
                <p className="body-large mb-6">
                  {organizationInfo.mission}
                </p>
                <p className="body-medium">
                  We create accessible, holistic recovery pathways that promote long-term wellness, 
                  housing stability, transportation access, and meaningful community connection.
                </p>
              </div>
              
              <div>
                <h2 className="heading-2 mb-6">Our Vision</h2>
                <p className="body-large mb-6">
                  A world where every person affected by substance use disorders has access to 
                  compassionate, evidence-based support that empowers lasting recovery and wellness.
                </p>
                <p className="body-medium">
                  We envision communities where recovery is celebrated, stigma is eliminated, 
                  and individuals are supported in rebuilding their lives with dignity and hope.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-8 text-center">Our Story</h2>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <p className="body-large mb-6">
                Saged Recovery was born from a simple yet powerful recognition: 
                traditional treatment models often fall short of addressing the complex, 
                ongoing needs of individuals in recovery.
              </p>
              <p className="body-medium mb-6">
                Founded by individuals with lived experience in recovery and community advocates, 
                our organization bridges the gap between traditional treatment and real-world recovery support.
              </p>
              <p className="body-medium">
                Today, we're growing to become a comprehensive resource for individuals and families
                across our region. Our commitment is unwavering: to offer hope, connection, and the
                tools necessary for sustainable recovery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-16">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Our Leadership Team</h2>
            <p className="body-large max-w-2xl mx-auto">
              Our diverse leadership team combines lived experience, professional credentials, 
              and community knowledge to guide our mission.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            {leadership.map((leader) => (
              <div key={leader.id} className="bg-white p-8 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex flex-col lg:flex-row gap-8">
                  <div className="lg:w-1/3 text-center">
                    {leader.photo ? (
                      <img
                        src={leader.photo}
                        alt={leader.name}
                        loading="lazy"
                        className="w-32 h-32 rounded-full object-cover border-4 border-green-100 mx-auto mb-4"
                      />
                    ) : (
                      <div className="w-32 h-32 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Users className="h-16 w-16 text-green-600" />
                      </div>
                    )}
                    <h3 className="heading-3 mb-2">{leader.name}</h3>
                    <p className="font-semibold mb-3" style={{color: 'var(--accent-text)'}}>{leader.title}</p>
                    <div className="pt-3 border-t border-gray-200">
                      <p className="caption font-medium">Credentials:</p>
                      <p className="caption">{leader.credentials}</p>
                    </div>
                  </div>
                  
                  <div className="lg:w-2/3">
                    <div className="space-y-4">
                      <p className="body-medium">{leader.bio}</p>
                      {leader.longBio && (
                        <p className="body-medium">{leader.longBio}</p>
                      )}
                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="body-small font-medium mb-2" style={{color: 'var(--text-primary)'}}>
                          Kelly's Personal Mission:
                        </p>
                        <p className="body-small italic">
                          "My mission is to ensure that no individual is denied recovery support due to barriers, 
                          stigma, or lack of resources. Through lived experience and professional expertise, 
                          I'm committed to building recovery-friendly communities where healing is possible for everyone."
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Board of Directors Preview */}
      <section className="py-16">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Our Board of Directors</h2>
            <p className="body-large max-w-2xl mx-auto">
              Meet the dedicated leaders guiding our mission and helping build recovery support for our community.
            </p>
          </div>

          <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {boardMembers.map((member) => (
              <div key={member.id} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm text-center">
                {member.photo ? (
                  <img
                    src={member.photo}
                    alt={member.name}
                    loading="lazy"
                    style={{ objectPosition: member.photoPosition || 'center' }}
                    className="w-24 h-24 rounded-full object-cover border-4 border-green-100 mx-auto mb-4"
                  />
                ) : (
                  <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-12 w-12 text-green-600" />
                  </div>
                )}
                <h3 className="service-card-title mb-1">{member.name}</h3>
                <p className="body-small font-medium" style={{ color: 'var(--accent-text)' }}>{member.title}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/board" className="btn-primary">Meet the Full Board</Link>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Our Core Values</h2>
            <p className="body-large max-w-2xl mx-auto">
              These values guide every interaction, decision, and service we provide. 
              They represent our unwavering commitment to those we serve.
            </p>
          </div>
          
          <div className="ai-grid">
            {values.map((value, index) => {
              const IconComponent = valueIcons[value.title] || Heart;
              return (
                <div key={index} className="service-card text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-4 rounded-full bg-green-100">
                      <IconComponent className="h-8 w-8 text-green-600" />
                    </div>
                  </div>
                  <h3 className="service-card-title mb-4">{value.title}</h3>
                  <p className="service-card-description">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="heading-2 mb-8">Our Recovery Philosophy</h2>
            <div className="bg-green-50 p-8 rounded-lg">
              <p className="body-large mb-6" style={{color: 'var(--text-primary)'}}>
                "At Saged, we believe recovery is not a one-size-fits-all process. 
                Healing requires stability, safety, connection, and purpose."
              </p>
              <p className="body-medium mb-6">
                Our trauma-informed, strength-based approach empowers individuals to rebuild their lives, 
                reconnect with their communities, and achieve sustainable recovery. Through compassion, 
                accountability, and hope, we support whole-person wellness and lasting transformation.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2" style={{color: 'var(--accent-text)'}}>Stability</div>
                  <p className="body-small">Safe housing and reliable support systems</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2" style={{color: 'var(--accent-text)'}}>Safety</div>
                  <p className="body-small">Trauma-informed, non-judgmental environment</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2" style={{color: 'var(--accent-text)'}}>Connection</div>
                  <p className="body-small">Meaningful relationships and community ties</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2" style={{color: 'var(--accent-text)'}}>Purpose</div>
                  <p className="body-small">Goals, meaning, and contributions to community</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Impact */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Community Impact</h2>
            <p className="body-large mb-8">
              Rooted in lived experience, professional leadership, and community collaboration, 
              Saged provides services designed to remove barriers to care and strengthen recovery outcomes.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
              <Heart className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="heading-3 mb-3">Community Partnerships</h3>
              <p className="body-medium">
                We collaborate with healthcare providers, social services, law enforcement, 
                and community organizations to provide comprehensive support.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
              <Users className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="heading-3 mb-3">Peer-Driven Model</h3>
              <p className="body-medium">
                Our services are led by individuals with lived recovery experience, 
                creating authentic connections and hope for lasting change.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
              <Award className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="heading-3 mb-3">Evidence-Based Care</h3>
              <p className="body-medium">
                All our services are grounded in proven methodologies and best practices 
                in addiction recovery and mental health support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-green-50">
        <div className="container text-center">
          <h2 className="heading-2 mb-4">Join Our Mission</h2>
          <p className="body-large mb-8 max-w-2xl mx-auto">
            Whether you're seeking support for yourself or a loved one, or looking to get involved 
            in our community, we invite you to be part of the recovery movement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`mailto:${organizationInfo.email}`} className="btn-primary flex items-center gap-2">
              Get Support Today
              <ArrowRight className="h-4 w-4" />
            </a>
            <a href="/get-involved" className="btn-secondary">
              Volunteer With Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;